import { useMemo } from 'react';

import { useTreatmentTypeSettings } from 'contexts/structure-edit/useTreatmentTypeSettings';
import { ModuleContainer, PageDto, UiModule } from 'data/generated/graphql';
import { useConvertedProperty } from 'hooks';

import { Structure } from 'utils/structure.utils';

/**
 * Patches root modules that are missing uiModuleType/uiModuleFields using the structure +
 * treatment-type catalog. This handles the case where structure pages are loaded with
 * uiModuleType stripped at rest and the backend hydration hasn't run yet (or was partial).
 *
 * Modules that already have uiModuleType are left untouched so no existing content is lost.
 */
const hydrateStructureModules = (
  root: PageDto['root'],
  structure: Structure,
  ttCatalog: ReturnType<typeof useTreatmentTypeSettings>
): PageDto['root'] => {
  if (root.collection.length === 0) return root;
  const pageContainer = root.collection[0];

  const modules = pageContainer.collection;
  const needsHydration = modules.some(
    (m) =>
      (m as ModuleContainer).slotRef &&
      !(m.attributes as { pageModule?: { uiModuleType?: unknown } }).pageModule?.uiModuleType
  );
  if (!needsHydration) return root;

  const hydratedModules = modules.map((m) => {
    const mod = m as ModuleContainer;
    if (!mod.slotRef || mod.attributes.pageModule?.uiModuleType) return m;

    const matchingSlot = structure.groups
      .find((g) => g.id === mod.groupId)
      ?.modules.find((s) => s.slotRef === mod.slotRef);
    const ttKey = matchingSlot?.ttKey ?? null;
    const slotUiModuleFields: UiModule | null | undefined = matchingSlot?.uiModuleFields;
    if (!ttKey) return m;

    const ttEntry = ttCatalog.find((t) => t.treatmentType === ttKey || t.treatmentTypeKey === ttKey);
    const uiModuleType = ttEntry?.module?.uiModuleType;
    if (!uiModuleType) return m;

    return {
      ...m,
      attributes: {
        ...mod.attributes,
        pageModule: {
          ...mod.attributes.pageModule,
          uiModuleType,
          uiModuleFields: {
            ...(ttEntry.module?.uiModuleFields ?? {}),
            ...(slotUiModuleFields
              ? Object.fromEntries(Object.entries(slotUiModuleFields).filter(([, v]) => v != null))
              : {}),
            ...(mod.attributes.pageModule?.uiModuleFields ?? {})
          }
        }
      }
    };
  });

  return {
    ...root,
    collection: [{ ...pageContainer, collection: hydratedModules }]
  } as PageDto['root'];
};

export function useSectionPageGroups(activePageData: PageDto | undefined) {
  const currentProperty = useConvertedProperty();
  const ttCatalog = useTreatmentTypeSettings(currentProperty ?? '');
  const effectiveStructure: Structure | undefined = activePageData?.structure ?? undefined;

  const hydratedRoot = useMemo(() => {
    if (!effectiveStructure || !activePageData?.root) return undefined;
    const patched = hydrateStructureModules(activePageData.root, effectiveStructure, ttCatalog);
    return patched === activePageData.root ? undefined : patched;
  }, [effectiveStructure, activePageData?.root, ttCatalog]);

  return { effectiveStructure, hydratedRoot };
}
